import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getService, getIntegrationInfo, getPlanPrices, PlanListItem } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, Package, Coins, Code, Copy, Check, ChevronDown, ChevronRight, DollarSign, Pencil, Layers } from 'lucide-react'
import { useState } from 'react'
import { PlanEditDrawer } from '@/components/admin/PlanEditDrawer'
import { FeatureDefinitionsPanel } from '@/components/admin/FeatureDefinitionsPanel'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}

function PlanRow({
  plan,
  serviceId,
  onEdit,
}: {
  plan: PlanListItem
  serviceId: string
  onEdit: (plan: PlanListItem) => void
}) {
  const [expanded, setExpanded] = useState(false)

  const { data: prices, isLoading } = useQuery({
    queryKey: ['plan-prices', serviceId, plan.id],
    queryFn: () => getPlanPrices(serviceId, plan.id),
    enabled: expanded,
  })

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <div>
              <div className="font-medium">{plan.name}</div>
              <div className="text-sm text-muted-foreground">
                {plan.code}
              </div>
            </div>
            {plan.is_recommended && (
              <Badge variant="default" className="bg-yellow-500">
                Recommended
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right font-medium">
          {formatCurrency(parseFloat(plan.price_monthly), plan.currency)}
        </TableCell>
        <TableCell className="text-right font-medium">
          {formatCurrency(parseFloat(plan.price_yearly), plan.currency)}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {Object.entries(plan.limits).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-xs">
                {key}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
              </Badge>
            ))}
          </div>
        </TableCell>
        <TableCell className="text-center">
          {plan.subscription_count}
        </TableCell>
        <TableCell className="text-center">
          {plan.is_active ? (
            <Badge variant="default" className="bg-green-500">
              Active
            </Badge>
          ) : (
            <Badge variant="secondary">
              Inactive
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(plan)
            }}
            className="gap-1"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 font-medium">
                <DollarSign className="h-4 w-4" />
                Multi-currency Prices
              </div>
              {isLoading ? (
                <div className="text-sm text-muted-foreground">Loading prices...</div>
              ) : prices && prices.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                  {prices.map((price) => (
                    <div
                      key={price.id}
                      className="flex items-center justify-between rounded-lg border bg-background p-3"
                    >
                      <div>
                        <div className="font-medium">
                          {formatCurrency(parseFloat(price.amount), price.currency)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {price.currency} / {price.interval}
                        </div>
                      </div>
                      {price.stripe_price_id ? (
                        <Badge variant="outline" className="text-xs">
                          Stripe
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          No Stripe
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No multi-currency prices configured. Using default plan prices.
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function ServiceDetail() {
  const { id } = useParams<{ id: string }>()
  const [selectedPlan, setSelectedPlan] = useState<PlanListItem | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: service, isLoading, error } = useQuery({
    queryKey: ['service', id],
    queryFn: () => getService(id!),
    enabled: !!id,
  })

  const { data: integrationInfo } = useQuery({
    queryKey: ['service-integration', id],
    queryFn: () => getIntegrationInfo(id!),
    enabled: !!id,
  })

  const handleEditPlan = (plan: PlanListItem) => {
    setSelectedPlan(plan)
    setDrawerOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/services">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Link>
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Error: {error.message}
        </div>
      </div>
    )
  }

  if (!service) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/services">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Link>
        </Button>
        <div className="rounded-lg border p-4 text-muted-foreground">
          Service not found
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/services">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{service.name}</h1>
            {service.is_active ? (
              <Badge variant="default" className="bg-green-500">Active</Badge>
            ) : (
              <Badge variant="destructive">Inactive</Badge>
            )}
          </div>
          <p className="text-muted-foreground">{service.code}</p>
        </div>
      </div>

      {/* Service Info Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="text-lg">
              {service.billing_type === 'subscription' ? 'Subscription' : 'Credits'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{service.subscription_count}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monthly Recurring Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {formatCurrency(parseFloat(service.mrr), 'EUR')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Callback Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Callback Configuration</span>
            {service.callback_enabled ? (
              <Badge variant="default" className="bg-green-500">Enabled</Badge>
            ) : (
              <Badge variant="secondary">Disabled</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {service.callback_url ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Callback URL</p>
              <div className="flex items-center gap-2 rounded bg-muted p-2 font-mono text-sm">
                <span className="flex-1">{service.callback_url}</span>
                <CopyButton text={service.callback_url} />
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">No callback URL configured</p>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Plans, Credit Packs, Integration */}
      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Plans ({service.plans.length})
          </TabsTrigger>
          <TabsTrigger value="credits" className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            Credit Packs ({service.credit_packs.length})
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Features
          </TabsTrigger>
          <TabsTrigger value="integration" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Integration
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <CardContent className="pt-6">
              {service.plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">No plans configured</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Monthly</TableHead>
                      <TableHead className="text-right">Yearly</TableHead>
                      <TableHead>Limits</TableHead>
                      <TableHead className="text-center">Subs</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {service.plans.map((plan) => (
                      <PlanRow
                        key={plan.id}
                        plan={plan}
                        serviceId={service.id}
                        onEdit={handleEditPlan}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Packs Tab */}
        <TabsContent value="credits">
          <Card>
            <CardContent className="pt-6">
              {service.credit_packs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No credit packs configured</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pack</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {service.credit_packs.map((pack) => (
                      <TableRow key={pack.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{pack.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {pack.code}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-lg font-bold">
                          {pack.credits}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(parseFloat(pack.price), pack.currency)}
                        </TableCell>
                        <TableCell className="text-center">
                          {pack.is_active ? (
                            <Badge variant="default" className="bg-green-500">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features">
          <Card>
            <CardContent className="pt-6">
              <FeatureDefinitionsPanel serviceId={service.id} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Tab */}
        <TabsContent value="integration">
          <div className="space-y-6">
            {integrationInfo && (
              <>
                {/* API Key */}
                <Card>
                  <CardHeader>
                    <CardTitle>API Key</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Header: <code className="bg-muted px-1 rounded">{integrationInfo.api_key_header}</code>
                      </p>
                      <div className="flex items-center gap-2 rounded bg-muted p-3 font-mono text-sm">
                        <span className="flex-1">{integrationInfo.api_key_example}</span>
                        <CopyButton text={integrationInfo.api_key_example} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Endpoints */}
                <Card>
                  <CardHeader>
                    <CardTitle>API Endpoints</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {integrationInfo.endpoints.map((endpoint, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 rounded border p-2"
                        >
                          <Badge
                            variant={endpoint.method === 'GET' ? 'outline' : 'default'}
                            className={endpoint.method === 'POST' ? 'bg-green-600' : ''}
                          >
                            {endpoint.method}
                          </Badge>
                          <code className="flex-1 text-sm">
                            {integrationInfo.base_url}{endpoint.path}
                          </code>
                          <span className="text-sm text-muted-foreground">
                            {endpoint.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Integration Flow */}
                <Card>
                  <CardHeader>
                    <CardTitle>Integration Flow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="whitespace-pre-wrap rounded bg-muted p-4 font-mono text-sm">
                      {integrationInfo.flow_description}
                    </pre>
                  </CardContent>
                </Card>

                {/* Documentation Links */}
                <Card>
                  <CardHeader>
                    <CardTitle>Documentation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {integrationInfo.documentation_links.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-blue-600 hover:underline"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Metadata */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-8 text-sm text-muted-foreground">
            <div>
              <span className="font-medium">Created:</span>{' '}
              {formatDate(service.created_at)}
            </div>
            {service.stripe_statement_descriptor && (
              <div>
                <span className="font-medium">Stripe Descriptor:</span>{' '}
                {service.stripe_statement_descriptor}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plan Edit Drawer */}
      <PlanEditDrawer
        plan={selectedPlan}
        serviceId={service.id}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}
